import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { customerAPI, auth } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Monitor, Trash2, Copy, Check } from 'lucide-react';

export default function LicenseDetails() {
  const navigate = useNavigate();
  const { licenseId } = useParams();
  const [license, setLicense] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  useEffect(() => {
    // Check if user is authenticated
    if (!auth.isAuthenticated()) {
      navigate('/login');
      return;
    }

    loadLicenseData();
  }, [licenseId, navigate]);

  const loadLicenseData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load license and devices in parallel
      const [licenseResponse, devicesResponse] = await Promise.all([
        customerAPI.getLicense(licenseId),
        customerAPI.getLicenseDevices(licenseId),
      ]);

      setLicense(licenseResponse.data);
      setDevices(devicesResponse.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load license data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateDevice = async () => {
    if (!deviceToDelete) return;

    try {
      setDeleting(true);
      await customerAPI.deactivateDevice(licenseId, deviceToDelete.device_id);
      
      // Reload data
      await loadLicenseData();
      
      // Close dialog
      setDeviceToDelete(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to deactivate device');
    } finally {
      setDeleting(false);
    }
  };

  const copyLicenseKey = () => {
    navigator.clipboard.writeText(license.license_key);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-12 w-64 mb-6 bg-purple-800/20" />
          <Skeleton className="h-64 mb-6 bg-purple-800/20" />
          <Skeleton className="h-96 bg-purple-800/20" />
        </div>
      </div>
    );
  }

  if (!license) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 pt-20 flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
          <CardHeader>
            <CardTitle className="text-white">License Not Found</CardTitle>
            <CardDescription className="text-gray-400">
              The license you're looking for doesn't exist or you don't have access to it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
              <Link to="/dashboard">Back to Dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 pt-20">
      {/* Header */}
      <div className="border-b border-purple-500/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" asChild className="mb-4 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10">
            <Link to="/dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Link>
          </Button>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">License Details</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <Alert variant="destructive" className="mb-6 bg-red-900/20 border-red-500/50 text-red-200">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* License Info Card */}
        <Card className="mb-6 bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">License Information</CardTitle>
              <div className="flex gap-2">
                <Badge variant={license.revoked ? 'destructive' : 'default'} className="bg-purple-600 text-white">
                  {license.tier.toUpperCase()}
                </Badge>
                {license.revoked && (
                  <Badge variant="destructive">REVOKED</Badge>
                )}
                {!license.revoked && !license.expires_at && (
                  <Badge variant="secondary" className="bg-green-600 text-white">LIFETIME</Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-purple-400">License Key</label>
              <div className="mt-1 font-mono text-sm bg-gray-800/50 p-3 rounded flex items-center justify-between border border-purple-500/20">
                <span className="break-all text-gray-300 flex-1 min-w-0">{license.license_key}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyLicenseKey}
                  className="ml-2 flex-shrink-0 hover:bg-purple-500/10"
                >
                  {copiedKey ? (
                    <Check className="h-4 w-4 text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4 text-purple-400" />
                  )}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-purple-400">Issued</label>
                <p className="mt-1 text-sm text-gray-300">{formatDate(license.issued_at)}</p>
              </div>

              {license.expires_at && (
                <div>
                  <label className="text-sm font-medium text-purple-400">Expires</label>
                  <p className="mt-1 text-sm text-gray-300">{formatDate(license.expires_at)}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-purple-400">Device Limit</label>
                <p className="mt-1 text-sm text-gray-300">
                  {license.active_devices} / {license.device_limit} devices
                </p>
              </div>
            </div>

            {license.revoked && license.revoked_reason && (
              <Alert variant="destructive" className="bg-red-900/20 border-red-500/50 text-red-200">
                <AlertDescription>
                  <strong>Revoked:</strong> {license.revoked_reason}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Devices Card */}
        <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
          <CardHeader>
            <CardTitle className="text-white">Active Devices</CardTitle>
            <CardDescription className="text-gray-400">
              Manage devices that have activated this license
            </CardDescription>
          </CardHeader>
          <CardContent>
            {devices.length === 0 ? (
              <div className="text-center py-12">
                <Monitor className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-white">No devices</h3>
                <p className="mt-1 text-sm text-gray-400">
                  No devices have been activated with this license yet.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {devices.map((device) => (
                  <div
                    key={device.id}
                    className="border border-purple-500/20 rounded-lg p-4 flex items-start justify-between bg-gray-800/30"
                  >
                    <div className="flex items-start gap-3">
                      <Monitor className="h-5 w-5 text-purple-400 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-white">{device.device_name}</h4>
                        <div className="mt-1 text-sm text-gray-400 space-y-1">
                          <p>Platform: {device.platform}</p>
                          <p>App Version: {device.app_version}</p>
                          <p>Activated: {formatDate(device.activated_at)}</p>
                          <p>Last Seen: {formatDate(device.last_seen_at)}</p>
                        </div>
                      </div>
                    </div>

                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeviceToDelete(device)}
                      disabled={license.revoked}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deviceToDelete} onOpenChange={() => setDeviceToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Device</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate "{deviceToDelete?.device_name}"? 
              This will free up a device slot for this license.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivateDevice}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deactivating...' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

